
import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart } from "@/components/ui/chart";

interface ClickData {
  url: string;
  count: number;
}

interface ClickAnalysisChartProps {
  clickData: ClickData[];
  isLoading: boolean;
  height?: number;
}

export function ClickAnalysisChart({ 
  clickData, 
  isLoading,
  height = 300 
}: ClickAnalysisChartProps) {
  // Format URLs for better display
  const formatUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      // Return path + up to 15 chars of query string
      let displayUrl = urlObj.pathname;
      if (urlObj.search && urlObj.search.length > 0) {
        displayUrl += urlObj.search.substring(0, 15);
        if (urlObj.search.length > 15) displayUrl += '...';
      }
      return displayUrl;
    } catch (e) {
      // If URL parsing fails, just truncate the string
      return url.length > 30 ? url.substring(0, 27) + '...' : url;
    }
  };

  if (isLoading) {
    return <Skeleton className={`w-full h-[${height}px]`} />;
  }

  // Prepare data for chart - show top 10 links with highest click counts
  const sortedData = [...clickData].sort((a, b) => b.count - a.count).slice(0, 10);

  const chartData = {
    labels: sortedData.map(item => formatUrl(item.url)),
    datasets: [
      {
        label: 'Clicks',
        data: sortedData.map(item => item.count),
        backgroundColor: 'rgba(99, 102, 241, 0.7)',
        borderColor: 'rgb(99, 102, 241)',
        borderWidth: 1,
      }
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: {
      tooltip: {
        callbacks: {
          title: (items: any) => {
            const index = items[0].dataIndex;
            // Show full URL in tooltip
            return sortedData[index].url;
          },
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    }
  };

  if (sortedData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[300px] text-center text-slate-500 border border-dashed border-slate-300 rounded-md">
        <p>No click data available for the selected period</p>
        <p className="text-sm">Try selecting a different date range or campaign</p>
      </div>
    );
  }

  return (
    <div style={{ height: `${height}px` }}>
      <BarChart 
        data={chartData} 
        options={chartOptions}
        className="w-full h-full" 
      />
    </div>
  );
}
